import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GraduationCap,
  Play,
  Calendar,
  ArrowRight,
  VideoCamera,
} from "@phosphor-icons/react";
import { LoginContext } from "context/Auth/LoginContext";
import MainButton from "Components/Button/MainButton";

const OwnedClasses = () => {
  const { user } = useContext(LoginContext);
  const { t } = useTranslation();
  const courses = user?.purchasedItems?.courses || [];

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  if (courses.length === 0) {
    return (
      <div className="font-text">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <GraduationCap size={24} className="text-primary" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">
            {t("account.myCourses")}
          </h2>
        </div>

        {/* Empty State */}
        <div className="text-center py-12 px-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <VideoCamera size={40} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("account.noClasses")}
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            {t("account.buyClassesInfo")}
          </p>
          <MainButton to="/courses">
            {t("account.buyClasses")}
          </MainButton>
        </div>
      </div>
    );
  }

  return (
    <div className="font-text">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <GraduationCap size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {t("account.myCourses")}
            </h2>
            <p className="text-sm text-gray-500">
              {courses.length} {courses.length === 1 ? "course" : "courses"}
            </p>
          </div>
        </div>
        <Link
          to="/courses"
          className="text-primary hover:text-primaryDark font-medium text-sm flex items-center gap-1"
        >
          {t("account.browseCourses") || "Browse more"}
          <ArrowRight size={16} />
        </Link>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <Link
            to={`/course-detailes/${course.slug}`}
            key={course._id || course.courseId}
            className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
          >
            {/* Thumbnail */}
            <div className="relative h-44 overflow-hidden bg-gray-100">
              {course.videoTrailer?.assets?.thumbnail ? (
                <img
                  src={course.videoTrailer.assets.thumbnail}
                  alt={course.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primaryDark/20">
                  <GraduationCap size={48} className="text-primary/50" />
                </div>
              )}
              {/* Play Overlay */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Play size={24} weight="fill" className="text-primary ml-1" />
                </div>
              </div>
              {/* Purchased Badge */}
              <div className="absolute top-3 left-3">
                <span className="px-2.5 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                  {t("account.purchased")}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                {course.title}
              </h3>

              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar size={16} />
                <span>
                  {t("account.purchasedOn") || "Purchased"}: {formatDate(course.purchasedAt)}
                </span>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-primary font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t("account.viewCourse")}
                  <ArrowRight size={16} />
                </span>
                {course.price && (
                  <span className="text-gray-400 text-sm line-through">
                    ${course.price}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default OwnedClasses;
